"use client";

import {useEffect, useRef} from "react";
import type {WorkflowEditorBinding} from "@/contexts/chat/WorkflowEditorBridgeContext";
import type {ConversationMessage} from "@/services/wingman";
import {confirmedDraftSaves} from "@/services/wingman/omega-results";

/**
 * Keeps the builder beside this chat in step with Wingman: the canvas is
 * read-only while a turn runs, and reloads when a turn saves its workflow.
 */
export function useWorkflowEditorSync({
   binding,
   conversationId,
   messages,
   historyLoaded,
   busy,
}: {
   binding: WorkflowEditorBinding | null;
   conversationId: string;
   messages: ConversationMessage[];
   historyLoaded: boolean;
   busy: boolean;
}) {
   useEffect(() => {
      if (!binding || !busy) return;
      return binding.holdCanvas();
   }, [binding, busy]);

   // Saves already in the loaded history are not news; only saves that arrive
   // after it are reloaded into the canvas.
   const seenSavesRef = useRef<Set<string> | null>(null);
   useEffect(() => {
      seenSavesRef.current = null;
   }, [conversationId]);

   useEffect(() => {
      if (!historyLoaded) return;
      const saves = confirmedDraftSaves(messages);
      if (seenSavesRef.current === null) {
         seenSavesRef.current = new Set(saves.map((save) => save.callId));
         return;
      }
      const seen = seenSavesRef.current;
      const fresh = saves.filter((save) => !seen.has(save.callId));
      fresh.forEach((save) => seen.add(save.callId));
      if (binding && fresh.some((save) => save.workflow.id === binding.workflowId)) void binding.onDraftSaved();
   }, [binding, historyLoaded, messages]);
}
